import React, {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import PoppaysLoader from '@/components/poppays-loader';
import useParams from '@/hooks/use-params';
import translate from '@/utils/translate';
import GigLoadError from '../components/GigLoadError';
import GigShell from '../shell/GigShell';
import {GigTab} from '../shell/gigTabs';
import useGigShellData from '../shell/useGigShellData';
import {captureDeliverablesError, trackDeliverables} from '../telemetry';
import BriefHtml from './BriefHtml';
import BriefSection from './BriefSection';
import DeliverablesSummary from './DeliverablesSummary';
import DiscountCodeItem from './DiscountCodeItem';
import EligibilityCard from './EligibilityCard';
import PreferredCreatorCard from './PreferredCreatorCard';
import PromotableUrlItem from './PromotableUrlItem';
import decodeHtmlEntities from './decodeHtmlEntities';
import styles from './GigBriefPage.module.scss';

/**
 * Renders only what Ember's brief.hbs shows a creator: the same `/paid_gigs/:id`
 * payload also carries brand-internal campaign figures (see informationTypes.ts).
 */
export default function GigBriefPage() {
  const {gigId} = useParams();
  const navigate = useNavigate();

  const {
    shell,
    brief,
    deliverables,
    totals,
    error,
    gigLoading,
    gigFailed,
    submissionsLoading,
    submissionsFailed,
    refetch,
  } = useGigShellData(gigId);

  useEffect(() => {
    trackDeliverables('Opened gig brief', {gigId});
  }, [gigId]);

  useEffect(() => {
    if (gigFailed || submissionsFailed) {
      captureDeliverablesError(error, 'Gig brief failed to load', {
        gigId,
        gigFailed,
        submissionsFailed,
      });
    }
  }, [error, gigFailed, submissionsFailed, gigId]);

  if (gigLoading) {
    return <PoppaysLoader />;
  }

  if (gigFailed) {
    return <GigLoadError onRetry={refetch} />;
  }

  const handleSelectKind = (tab: GigTab) => {
    if (tab.to) {
      navigate(tab.to);
    }
  };

  return (
    <GigShell
      gigId={gigId}
      activeKind="brief"
      shell={shell}
      deliverables={deliverables}
      totals={totals}
      showProgress={!submissionsLoading && !submissionsFailed}
      onSelectKind={handleSelectKind}
    >
      <div className={styles.page}>
        <header className={styles.contentHead}>
          <h1 className={styles.title}>{translate('Gig Brief')}</h1>
        </header>

        <EligibilityCard eligibility={brief.eligibility} />
        <PreferredCreatorCard
          preferredCreator={brief.preferredCreator}
          creatorIsInTheZone={brief.creatorIsInTheZone}
        />
        <DeliverablesSummary
          counts={shell.counts}
          networkName={shell.socialNetworkName}
          deliverables={deliverables}
        />

        {brief.descriptionHtml ? (
          <div className={styles.description} data-testid="brief-description">
            <BriefHtml contentHtml={brief.descriptionHtml} />
          </div>
        ) : null}

        {brief.showPromotableUrls && (
          <PromotableUrlItem links={brief.promotableLinks} />
        )}

        {brief.discountCode ? (
          <DiscountCodeItem code={brief.discountCode} />
        ) : null}

        {brief.briefItems.map((item) => (
          <BriefSection
            key={item.id}
            testID="brief-item"
            title={decodeHtmlEntities(item.title)}
          >
            <BriefHtml
              contentHtml={item.contentHtml}
              highlighted={item.highlighted}
            />
          </BriefSection>
        ))}
      </div>
    </GigShell>
  );
}
